import { useMemo, useState } from "react";
import ConversationItem from "./ConversationItem";
import NewConversationModal from "./NewConversationModal";
import { Search } from "../../../svg";
import Skeleton from "react-loading-skeleton";
import "./style.css";

export default function ConversationsList({
  conversations,
  selectedConversation,
  onConversationSelect,
  onStartNewConversation,
  searchQuery: controlledSearchQuery,
  onSearchChange,
  currentUser,
  loading,
  onlineUsers,
  onConversationDeleted,
  typingUsers,
}) {
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const searchQuery =
    controlledSearchQuery !== undefined ? controlledSearchQuery : localSearchQuery;

  const handleSearchChange = (value) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setLocalSearchQuery(value);
    }
  };

  const handleNewConversation = (participantId) => {
    onStartNewConversation(participantId);
    setShowNewConversationModal(false);
  };

  const filteredConversations = useMemo(() => {
    const query = (searchQuery || "").trim().toLowerCase();
    const list = Array.isArray(conversations) ? [...conversations] : [];

    list.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0);
    });

    if (!query) return list;

    return list.filter((conversation) => {
      if (conversation.type === "group") {
        return (conversation.name || "").toLowerCase().includes(query);
      }
      const otherUser = conversation.participants?.find(
        (p) => p._id !== currentUser?.id
      );
      const fullName = `${otherUser?.first_name || ""} ${otherUser?.last_name || ""}`
        .trim()
        .toLowerCase();
      const username = (otherUser?.username || "").toLowerCase();
      return fullName.includes(query) || username.includes(query);
    });
  }, [conversations, searchQuery, currentUser?.id]);

  return (
    <div className="conversations_list">
      <div className="conversations_header">
        <div className="conversations_title">
          <h2>Chats</h2>
          <button
            type="button"
            className="new_conversation_btn"
            onClick={() => setShowNewConversationModal(true)}
            title="New conversation"
            aria-label="Start new conversation"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
          </button>
        </div>

        <div className={`conversations_search_bar${searchFocused ? " is_focused" : ""}`}>
          <span className="conversations_search_icon" aria-hidden="true">
            <Search color="#9CA3AF" />
          </span>
          <input
            type="text"
            className="conversations_search_input"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        </div>
      </div>

      <div className="conversations_content">
        {loading ? (
          <div className="conversations_skeleton">
            {Array(10)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="conversation_skeleton">
                  <Skeleton circle height={48} width={48} />
                  <div className="conversation_skeleton_content">
                    <Skeleton height={14} width="60%" />
                    <Skeleton height={12} width="80%" />
                  </div>
                </div>
              ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="no_conversations">
            <div className="no_conversations_content">
              <div className="no_conversations_icon">
                <i className="messenger_icon"></i>
              </div>
              <h3>
                {searchQuery ? "No chats found" : "No conversations yet"}
              </h3>
              <p>
                {searchQuery
                  ? `No results for "${searchQuery}"`
                  : "Start a new conversation to begin chatting"}
              </p>
              {!searchQuery && (
                <button
                  type="button"
                  className="start_conversation_btn"
                  onClick={() => setShowNewConversationModal(true)}
                >
                  Start New Conversation
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <ConversationItem
              key={conversation._id}
              conversation={conversation}
              currentUser={currentUser}
              isSelected={selectedConversation?._id === conversation._id}
              onSelect={() => onConversationSelect(conversation)}
              isOnline={
                conversation.type === "private" &&
                (() => {
                  const otherUser = conversation.participants.find(
                    (p) => p._id !== currentUser.id
                  );
                  if (
                    !otherUser ||
                    otherUser.isDeactivated ||
                    !onlineUsers ||
                    !onlineUsers.size
                  ) {
                    return false;
                  }
                  const otherUserId = String(otherUser._id);
                  return Array.from(onlineUsers).some(
                    (id) => String(id) === otherUserId
                  );
                })()
              }
              onConversationDeleted={onConversationDeleted}
              typingUsers={typingUsers?.[String(conversation._id)] || {}}
            />
          ))
        )}
      </div>

      {showNewConversationModal && (
        <NewConversationModal
          currentUser={currentUser}
          onlineUsers={onlineUsers}
          onStartConversation={handleNewConversation}
          onClose={() => setShowNewConversationModal(false)}
        />
      )}
    </div>
  );
}
